/**
 * Comms API layer: calls the generated SDK (backed by the FastAPI `/comms`
 * and `/admin/demo` routes) and maps its response shapes onto this feature's
 * `types.ts` — so `hooks.ts` and every screen under `features/ordering` and
 * `features/share` didn't need to change once the backend landed.
 *
 * Every exported function here mirrors a generated SDK call 1:1; see the
 * `sdk` import alias below for which one.
 */

import {
  approveNudge as sdkApproveNudge,
  CustomerDetail as SdkCustomerDetail,
  CustomerOrder as SdkCustomerOrder,
  CustomerSummary as SdkCustomerSummary,
  demoNextEvent as sdkDemoNextEvent,
  DemoQueueStatus as SdkDemoQueueStatus,
  demoReset as sdkDemoReset,
  DraftState as SdkDraftState,
  EmailRecordOut as SdkEmailRecordOut,
  getCustomerPreferences as sdkGetCustomerPreferences,
  getDemoQueue as sdkGetDemoQueue,
  getOrderingCustomer as sdkGetOrderingCustomer,
  getShareAccount as sdkGetShareAccount,
  getShareDraft as sdkGetShareDraft,
  getShareQuarters as sdkGetShareQuarters,
  getShareSummary as sdkGetShareSummary,
  listOrderEmails as sdkListOrderEmails,
  listOrderingCustomers as sdkListOrderingCustomers,
  listShareNudges as sdkListShareNudges,
  NextEventResult as SdkNextEventResult,
  NudgeHistoryItem as SdkNudgeHistoryItem,
  OrderingCustomer as SdkOrderingCustomer,
  OrderingCustomersPage as SdkOrderingCustomersPage,
  ShareAccountDetail as SdkShareAccountDetail,
  ShareSummary as SdkShareSummary,
  ShareSummaryRow as SdkShareSummaryRow,
  skipNudge as sdkSkipNudge,
  TierRef as SdkTierRef,
  updateCustomerPreferences as sdkUpdateCustomerPreferences,
} from "@/lib/api";
import type { ApiError as SdkApiError } from "@/lib/api/client";
import { logger } from "@/lib/logger";

import type {
  ApproveNudgeResult,
  CustomerDetail,
  CustomerOrder,
  CustomerSummary,
  DemoQueueStatus,
  DraftState,
  EmailRecord,
  NextEventResult,
  NotificationPreferences,
  NudgeHistoryItem,
  OrderingCustomer,
  OrderingCustomersPage,
  PageParams,
  ShareAccountDetail,
  ShareQuarters,
  ShareRange,
  ShareSummary,
  ShareSummaryRow,
  SkipNudgeResult,
  TierRef,
} from "./types";

/** Error shaped like the backend's — carries the HTTP status. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Await a generated SDK call and unwrap its `.data`, translating a thrown
 * `client.ts` error interceptor object (`{ status, body }`) into an
 * `ApiError` instance — the shape every screen's `instanceof` checks (e.g.
 * `DraftDrawer`'s 409 handling) expect.
 */
async function call<T>(promise: Promise<{ data?: T }>): Promise<T> {
  try {
    const { data } = await promise;
    if (data === undefined) {
      throw new ApiError(500, "Empty response body");
    }
    return data;
  } catch (err) {
    if (isSdkApiError(err)) {
      throw new ApiError(err.status, detailMessage(err.body, err.status));
    }
    throw err;
  }
}

/** Same as `call`, for endpoints with no response body (e.g. 204 No Content). */
async function callVoid(promise: Promise<{ data?: void }>): Promise<void> {
  try {
    await promise;
  } catch (err) {
    if (isSdkApiError(err)) {
      throw new ApiError(err.status, detailMessage(err.body, err.status));
    }
    throw err;
  }
}

function isSdkApiError(err: unknown): err is SdkApiError {
  return typeof err === "object" && err !== null && "status" in err && "body" in err;
}

/** FastAPI's `{ detail: "..." }` shape, when present, else a generic fallback. */
function detailMessage(body: unknown, status: number): string {
  if (body && typeof body === "object" && "detail" in body) {
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === "string") {
      return detail;
    }
  }
  return `Request failed with status ${status}`;
}

// ── Mapping helpers ──────────────────────────────────────────────────────
// The generated types track the FastAPI Pydantic models field-for-field, but
// mark every nullable field `| null` where this feature's hand-written types
// use `undefined` for "absent" — these fold `null` to `undefined` on the way
// in so components only ever deal with one "missing" value.

function toTierRef(tier: SdkTierRef | null | undefined): TierRef | undefined {
  return tier ?? undefined;
}

function toOrderingCustomer(c: SdkOrderingCustomer): OrderingCustomer {
  return {
    id: c.id,
    name: c.name,
    account_number: c.account_number ?? null,
    attention: c.attention,
    orders_in_progress: c.orders_in_progress,
    opted_in: c.opted_in,
    excluded: c.excluded,
    provenance: c.provenance,
  };
}

function toOrderingCustomersPage(p: SdkOrderingCustomersPage): OrderingCustomersPage {
  return {
    items: p.items.map(toOrderingCustomer),
    total: p.total,
    limit: p.limit,
    offset: p.offset,
  };
}

function toCustomerSummary(s: SdkCustomerSummary): CustomerSummary {
  return {
    name: s.name,
    orders_in_progress: s.orders_in_progress,
    emails_sent: s.emails_sent,
    provenance: s.provenance,
  };
}

function toCustomerOrder(o: SdkCustomerOrder): CustomerOrder {
  return {
    id: o.id,
    number: o.number,
    reached: o.reached,
    backordered: o.backordered,
    email_count: o.email_count,
    provenance: o.provenance,
  };
}

function toCustomerDetail(d: SdkCustomerDetail): CustomerDetail {
  return {
    summary: toCustomerSummary(d.summary),
    orders: d.orders.map(toCustomerOrder),
  };
}

function toEmailRecord(e: SdkEmailRecordOut): EmailRecord {
  return {
    // Order emails always carry an event_type — only share-nudge emails
    // (a different endpoint, not this one) go out with it null.
    event_type: e.event_type ?? "order_received",
    sent_at: e.sent_at,
    to: e.to,
    from: e.from,
    cc: e.cc ?? undefined,
    body: e.body,
    status: e.status,
    suppression_reason: e.suppression_reason ?? undefined,
    provenance: e.provenance,
  };
}

function toShareSummaryRow(r: SdkShareSummaryRow): ShareSummaryRow {
  return {
    id: r.id,
    name: r.name,
    status: r.status,
    share_before: r.share_before,
    comms: r.comms,
    tier_held: r.tier_held ?? null,
    target_tier: toTierRef(r.target_tier),
    gap: r.gap ?? undefined,
    share_final: r.share_final ?? undefined,
    difference: r.difference ?? undefined,
    provenance: r.provenance,
  };
}

function toShareSummary(s: SdkShareSummary): ShareSummary {
  return {
    mode: s.mode,
    kpis: { ...s.kpis },
    rows: s.rows.map(toShareSummaryRow),
    total: s.total,
    limit: s.limit,
    offset: s.offset,
  };
}

function toShareAccountDetail(d: SdkShareAccountDetail): ShareAccountDetail {
  return {
    name: d.name,
    kpis: {
      tier_held: d.kpis.tier_held ?? null,
      target_tier: { ...d.kpis.target_tier },
      nudges_sent: d.kpis.nudges_sent,
      avg_share_diff_after_nudge: d.kpis.avg_share_diff_after_nudge,
    },
    about: d.about,
    rep: d.rep,
    series: d.series.map((p) => ({ ...p })),
    markers: d.markers.map((m) => ({ ...m })),
    mix: { ...d.mix },
    pending: d.pending,
    provenance: d.provenance,
  };
}

function toNudgeHistoryItem(item: SdkNudgeHistoryItem): NudgeHistoryItem {
  return {
    quarter: item.quarter,
    subject: item.subject,
    body: item.body,
    sent_at: item.sent_at ?? undefined,
    delivery: item.delivery,
    share_before: item.share_before,
    share_final: item.share_final ?? undefined,
    difference: item.difference ?? undefined,
  };
}

function toDraftState(d: SdkDraftState): DraftState {
  return {
    state: d.state,
    subject: d.subject ?? undefined,
    body: d.body ?? undefined,
    date: d.date ?? undefined,
  };
}

function toNextEventResult(r: SdkNextEventResult): NextEventResult {
  return {
    order_number: r.order_number,
    event_type: r.event_type,
    outcome: r.outcome,
    suppression_reason: r.suppression_reason ?? undefined,
    account_id: r.account_id,
    account_name: r.account_name,
    processed_at: r.processed_at,
    to: r.to,
    cc: r.cc ?? undefined,
    step: r.step ?? undefined,
    total_steps: r.total_steps ?? undefined,
  };
}

function toDemoQueueStatus(s: SdkDemoQueueStatus): DemoQueueStatus {
  return {
    remaining: s.remaining,
    next: s.next
      ? {
          order_number: s.next.order_number,
          event_type: s.next.event_type,
          account_name: s.next.account_name,
        }
      : undefined,
  };
}

// ── Ordering events ──────────────────────────────────────────────────────

export async function listOrderingCustomers(
  params: PageParams = {},
): Promise<OrderingCustomersPage> {
  const page = await call(
    sdkListOrderingCustomers({
      query: {
        limit: params.limit,
        offset: params.offset,
        q: params.q,
        attention: params.attention,
      },
    }),
  );
  return toOrderingCustomersPage(page);
}

export async function getOrderingCustomer(customerId: string): Promise<CustomerDetail> {
  const detail = await call(sdkGetOrderingCustomer({ path: { account_id: customerId } }));
  return toCustomerDetail(detail);
}

export async function listOrderEmails(orderId: string): Promise<EmailRecord[]> {
  const emails = await call(sdkListOrderEmails({ path: { order_id: orderId } }));
  return emails.map(toEmailRecord);
}

export async function getCustomerPreferences(customerId: string): Promise<NotificationPreferences> {
  return call(sdkGetCustomerPreferences({ path: { account_id: customerId } }));
}

export async function updateCustomerPreferences(
  customerId: string,
  preferences: NotificationPreferences,
): Promise<NotificationPreferences> {
  const updated = await call(
    sdkUpdateCustomerPreferences({ path: { account_id: customerId }, body: preferences }),
  );
  logger.debug("[comms]", "preferences updated", customerId, updated);
  return updated;
}

// ── Order share ──────────────────────────────────────────────────────────

export async function getShareQuarters(): Promise<ShareQuarters> {
  return call(sdkGetShareQuarters());
}

export async function getShareSummary(
  quarter: string,
  params: PageParams = {},
): Promise<ShareSummary> {
  const summary = await call(
    sdkGetShareSummary({
      query: { quarter, limit: params.limit, offset: params.offset, q: params.q },
    }),
  );
  return toShareSummary(summary);
}

export async function getShareAccount(
  accountId: string,
  range: ShareRange = "3",
): Promise<ShareAccountDetail> {
  const detail = await call(
    sdkGetShareAccount({ path: { account_id: accountId }, query: { range } }),
  );
  return toShareAccountDetail(detail);
}

export async function listShareNudges(accountId: string): Promise<NudgeHistoryItem[]> {
  const history = await call(sdkListShareNudges({ path: { account_id: accountId } }));
  return history.map(toNudgeHistoryItem);
}

export async function getShareDraft(accountId: string): Promise<DraftState> {
  const draft = await call(sdkGetShareDraft({ path: { account_id: accountId } }));
  return toDraftState(draft);
}

export async function approveNudge(accountId: string): Promise<ApproveNudgeResult> {
  const result = await call(sdkApproveNudge({ path: { account_id: accountId } }));
  logger.debug("[comms]", "nudge approved", accountId, result.sent_at);
  return result;
}

export async function skipNudge(accountId: string): Promise<SkipNudgeResult> {
  const result = await call(sdkSkipNudge({ path: { account_id: accountId } }));
  logger.debug("[comms]", "nudge skipped", accountId, result.skipped_at);
  return result;
}

// ── Demo controls ────────────────────────────────────────────────────────

export async function demoReset(): Promise<void> {
  await callVoid(sdkDemoReset());
  logger.debug("[comms]", "demo world reset");
}

export async function demoNextEvent(): Promise<NextEventResult> {
  const result = await call(sdkDemoNextEvent());
  logger.debug("[comms]", "demo event processed", result.order_number, result.event_type, {
    outcome: result.outcome,
  });
  return toNextEventResult(result);
}

export async function getDemoQueue(): Promise<DemoQueueStatus> {
  const status = await call(sdkGetDemoQueue());
  return toDemoQueueStatus(status);
}
